import Container from '../../../../layout/Container/Container';
import { Button, Result, Skeleton, Tag, Space, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useGet } from '../../../../hooks/useApi';
import OnError from '../../../../components/OnError';
import { useGet as useGetLocalSchedule } from '../../../../hooks/localSchedule/useGet';
import { useClear as useClearLocalSchedule } from '../../../../hooks/localSchedule/useClear';
import { useGetStudents } from '../../../../hooks/useGetStudents';
import {
  getSubsidyType,
  getSubsidyDisplayInfo,
  SubsidyType,
} from '../../../../utils/subsidy';
import { useEffect } from 'react';
import { getCheckoutSummary } from '../../../../utils/checkoutSummary';

const { Paragraph, Text } = Typography;

interface CompleteProps {
  paymentIntentId: string;
}

const Complete = ({ paymentIntentId }: CompleteProps) => {
  const navigate = useNavigate();
  const localSchedule = useGetLocalSchedule(true);
  const clearLocalSchedule = useClearLocalSchedule();
  const { data: students } = useGetStudents();

  const useConfirm = useGet(
    `/v1/auth/reg/register-schedule/complete/${paymentIntentId}`
  );

  // The cart is usually cleared before this page (after payment or contract
  // signing), so fall back to the copy saved at registration.
  const selections: any[] =
    localSchedule?.selections ??
    getCheckoutSummary(paymentIntentId)?.selections ??
    [];

  // Get subsidy information from enrolled students
  const firstStudentId = selections[0]?.studentId;
  const student = students?.find((s: any) => +s.id === +firstStudentId);
  const subsidyType = getSubsidyType(student?.studentsSubsidyEligible);
  const subsidyInfo = getSubsidyDisplayInfo(
    subsidyType,
    student?.studentsSubsidyEligible?.SubsidyProgram?.programName
  );

  // Calculate total fees from selections
  const totalFees = selections.reduce(
    (sum: number, selection: any) => sum + (selection.fee || 0),
    0
  );

  // Clear the localSchedule when confirmation is successful
  useEffect(() => {
    if (!useConfirm.isLoading && !useConfirm.isError && localSchedule) {
      // Completely clear the localSchedule
      clearLocalSchedule();
    }
  }, [
    useConfirm.isLoading,
    useConfirm.isError,
    localSchedule,
    clearLocalSchedule,
  ]);

  if (useConfirm.isError) {
    return (
      <OnError
        title={`Error`}
        subTitle="Error confirming schedule"
        extra={
          <Button type="dashed" onClick={() => navigate('/scheduling')}>
            Go Back
          </Button>
        }
        status={404}
      />
    );
  }

  // Generate confirmation message based on subsidy type
  const getConfirmationMessage = () => {
    if (subsidyType === SubsidyType.NONE) {
      return (
        <Space direction="vertical" size="small">
          <Paragraph>
            Your schedule is complete. Payment confirmation has been sent to
            your email.
          </Paragraph>
        </Space>
      );
    }

    // For subsidized students
    const orgName =
      student?.studentsSubsidyEligible?.SubsidyProgram?.programName;

    return (
      <Space direction="vertical" size="middle" style={{ textAlign: 'center' }}>
        <Paragraph>Your schedule is complete!</Paragraph>

        {subsidyType === SubsidyType.SCHOLARSHIP ? (
          <Space direction="vertical" size="small">
            <Tag color={subsidyInfo.color}>{subsidyInfo.label}</Tag>
            <Text type="secondary">No charge - Scholarship applied</Text>
          </Space>
        ) : subsidyType === SubsidyType.ELOP ? (
          <Space direction="vertical" size="small">
            <Tag color={subsidyInfo.color}>{subsidyInfo.label}</Tag>
            <Text type="secondary">
              No payment required - Billed to {orgName || 'organization'}
            </Text>
          </Space>
        ) : (
          // PCOE or Other subsidy
          <Space direction="vertical" size="small">
            <Tag color={subsidyInfo.color}>
              {subsidyInfo.label} - {subsidyInfo.description}
            </Tag>
            <Text>
              Camp Fee: <Text strong>${totalFees.toFixed(2)}</Text>
            </Text>
            <Text type="secondary">
              No payment required - {orgName || 'Organization'} will be billed
            </Text>
          </Space>
        )}

        <Paragraph type="secondary" style={{ marginTop: 16 }}>
          Confirmation has been sent to your email.
        </Paragraph>
      </Space>
    );
  };

  return (
    <Container>
      {useConfirm.isLoading ? (
        <div role="progressbar">
          <Skeleton active />
        </div>
      ) : (
        <Result
          status="success"
          title="Enrollment Confirmed"
          subTitle={getConfirmationMessage()}
          extra={
            <Button type="default" onClick={() => navigate('/')}>
              Back To Home Page
            </Button>
          }
        />
      )}
    </Container>
  );
};

export default Complete;
